import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import {
  BsArrowLeft,
  BsArrowRight,
  BsCalendar3,
  BsCamera,
  BsChatLeftText,
  BsCheck2Circle,
  BsCircle,
  BsImages,
  BsPeople,
  BsPlusLg,
  BsTrash,
  BsXLg,
} from "react-icons/bs";

import ModalSlideOver from "../cardmodal/ModalSlideOver";

import {
  serviceAddScaleFitting,
  serviceUpdateScaleFitting,
  serviceRemoveCommentImageFitting,
} from "../../api/serviceApi";

import {
  serviceDeletePhoto,
  serviceUploadPhoto,
} from "../../api/servicePhotoApi";

import { useUsers } from "../../hooks/useUsers";
import { getImageUrl } from "../../utils/helper";

import "./jobsheetdrawerfitting.css";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

// ============================================================
// IMAGE CONFIGURATION
// ============================================================

const IMAGE_FIELDS = [
  {
    key: "beforePhotos",
    label: "Before Photos",
    description: "Photos taken before fitting work begins.",
  },
  {
    key: "afterPhotos",
    label: "After Photos",
    description: "Photos showing the completed fitting.",
  },
  {
    key: "vinPlateSticker",
    label: "VIN Plate / Sticker",
    description: "Photos of the VIN plate and identification sticker.",
  },
  {
    key: "scaleHeadUnit",
    label: "Scale Head Unit",
    description: "Photos of the installed scale head unit.",
  },
  {
    key: "truckCleaned",
    label: "Truck Cleaned",
    description: "Photos confirming the truck was left clean.",
  },
  {
    key: "workAreaCleaned",
    label: "Work Area Cleaned",
    description: "Photos confirming the work area was cleaned.",
  },
];

const CHECKLIST_FIELDS = [
  {
    imageKey: "beforePhotos",
    label: "Before photos taken",
  },
  {
    imageKey: "afterPhotos",
    label: "After photos taken",
  },
  {
    imageKey: "vinPlateSticker",
    label: "VIN plate / sticker photos taken",
  },
  {
    imageKey: "scaleHeadUnit",
    label: "Scale head unit photos taken",
  },
  {
    imageKey: "truckCleaned",
    label: "Truck cleaned photos taken",
  },
  {
    imageKey: "workAreaCleaned",
    label: "Work area cleaned",
  },
];

// ============================================================
// HELPERS
// ============================================================

const createEmptyImages = () => ({
  beforePhotos: [],
  afterPhotos: [],
  vinPlateSticker: [],
  scaleHeadUnit: [],
  truckCleaned: [],
  workAreaCleaned: [],
});

const toDateInput = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};

const getId = (item) => item?.id || item?._id || "";

const getUserLabel = (user) => {
  if (user?.name) return user.name;
  if (user?.fullName) return user.fullName;
  if (user?.username) return user.username;
  if (user?.email) return user.email;

  return getId(user);
};

const getImageUrlFromValue = (value) => {
  if (typeof value === "string") {
    return value;
  }

  return value?.imageUrl || value?.url || "";
};

const normalizeImageGroup = (values, category) => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((value, index) => {
      const url = getImageUrlFromValue(value);

      if (!url) {
        return null;
      }

      return {
        id: `existing-${category}-${index}-${url}`,
        type: "existing",
        url,
      };
    })
    .filter(Boolean);
};

const normalizeFittingImages = (fittingImages) => {
  const normalized = createEmptyImages();

  IMAGE_FIELDS.forEach(({ key }) => {
    normalized[key] = normalizeImageGroup(fittingImages?.[key], key);
  });

  return normalized;
};

const releaseLocalPreviews = (images) => {
  if (!images) {
    return;
  }

  IMAGE_FIELDS.forEach(({ key }) => {
    (images[key] || []).forEach((image) => {
      if (image.type === "local" && image.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }
    });
  });
};

const makeLocalId = () => {
  if (crypto?.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isImageFile = (file) => {
  if (file.type?.startsWith("image/")) {
    return true;
  }

  return /\.(jpg|jpeg|png|webp|heic|heif|avif)$/i.test(file.name || "");
};

// ============================================================
// COMPONENT
// ============================================================

const JobSheetDrawerFitting = ({ open, jobId, fitting, onClose, onSaved }) => {
  const imagesRef = useRef(createEmptyImages());

  // ==========================================================
  // SHARED USERS
  // ==========================================================

  const { users, loadingUsers } = useUsers(open);

  // ==========================================================
  // GENERAL STATE
  // ==========================================================

  const [submitting, setSubmitting] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(null);

  const [fitterIds, setFitterIds] = useState([]);

  const [fittingDate, setFittingDate] = useState("");

  // ==========================================================
  // IMAGE STATE
  // ==========================================================

  const [fittingImages, setFittingImages] = useState(createEmptyImages());

  /*
   * Saved image URLs removed by the user,
   * grouped exactly how the removal API expects.
   */
  const [removedExistingImages, setRemovedExistingImages] = useState([]);

  // ==========================================================
  // COMMENT STATE
  // ==========================================================

  /*
   * comments:
   *
   * {
   *   id,
   *   text,
   *   type: "existing" | "new"
   * }
   */
  const [comments, setComments] = useState([]);

  const [commentDraft, setCommentDraft] = useState("");

  /*
   * Existing saved comments removed by the
   * user. API accepts the actual strings.
   */
  const [removedExistingComments, setRemovedExistingComments] = useState([]);

  // ==========================================================
  // IMAGE VIEWER STATE
  // ==========================================================

  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);

  const [enlargedImageIndex, setEnlargedImageIndex] = useState(0);

  const editing = Boolean(fitting);

  // ==========================================================
  // KEEP IMAGE REF FOR OBJECT URL CLEANUP
  // ==========================================================

  useEffect(() => {
    imagesRef.current = fittingImages;
  }, [fittingImages]);

  useEffect(() => {
    return () => {
      releaseLocalPreviews(imagesRef.current);
    };
  }, []);

  // ==========================================================
  // LOAD / RESET FITTING
  // ==========================================================

  useEffect(() => {
    if (!open) {
      return;
    }

    setFitterIds(Array.isArray(fitting?.fitterIds) ? fitting.fitterIds : []);

    setFittingDate(toDateInput(fitting?.fittingDate));

    setFittingImages((current) => {
      releaseLocalPreviews(current);

      return normalizeFittingImages(fitting?.fittingImages);
    });

    setRemovedExistingImages([]);
    setRemovedExistingComments([]);

    setComments(
      Array.isArray(fitting?.comments)
        ? fitting.comments
            .map((comment) => {
              const text = String(comment || "").trim();

              if (!text) {
                return null;
              }

              return {
                id: makeLocalId(),
                text,
                type: "existing",
              };
            })
            .filter(Boolean)
        : [],
    );

    setCommentDraft("");

    setUploadProgress(null);

    setEnlargedImageUrl(null);
    setEnlargedImageIndex(0);
  }, [open, fitting]);

  // ==========================================================
  // FITTERS
  // ==========================================================

  const selectedUsers = useMemo(() => {
    return users.filter((user) => fitterIds.includes(getId(user)));
  }, [users, fitterIds]);

  const addFitter = (userId) => {
    if (!userId || submitting) {
      return;
    }

    setFitterIds((current) => {
      if (current.includes(userId)) {
        return current;
      }

      return [...current, userId];
    });
  };

  const removeFitter = (userId) => {
    if (submitting) {
      return;
    }

    setFitterIds((current) => current.filter((id) => id !== userId));
  };

  // ==========================================================
  // COMMENTS
  // ==========================================================

  const addComment = () => {
    if (submitting) {
      return;
    }

    const text = commentDraft.trim();

    if (!text) {
      return;
    }

    setComments((current) => [
      ...current,
      {
        id: makeLocalId(),
        text,
        type: "new",
      },
    ]);

    setCommentDraft("");
  };

  const removeComment = (commentId) => {
    if (submitting) {
      return;
    }

    const comment = comments.find((item) => item.id === commentId);

    if (!comment) {
      return;
    }

    /*
     * Existing comment:
     * queue it for backend removal.
     *
     * New comment:
     * simply remove it locally.
     */
    if (comment.type === "existing") {
      setRemovedExistingComments((current) => [...current, comment.text]);
    }

    setComments((current) => current.filter((item) => item.id !== commentId));
  };

  // ==========================================================
  // PHOTO SELECTION
  // ==========================================================

  const handleFiles = (category, event) => {
    const files = Array.from(event.target.files || []);

    /*
     * Allows the same file/photo to be
     * selected again later.
     */
    event.target.value = "";

    if (files.length === 0) {
      return;
    }

    const validFiles = [];

    let invalidFiles = 0;
    let oversizedFiles = 0;

    files.forEach((file) => {
      if (!isImageFile(file)) {
        invalidFiles += 1;
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        oversizedFiles += 1;
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles > 0) {
      toast.error(
        `${invalidFiles} file${
          invalidFiles === 1 ? "" : "s"
        } skipped because they were not valid images.`,
      );
    }

    if (oversizedFiles > 0) {
      toast.error(
        `${oversizedFiles} image${
          oversizedFiles === 1 ? "" : "s"
        } skipped because the maximum size is 5MB.`,
      );
    }

    if (validFiles.length === 0) {
      return;
    }

    const newImages = validFiles.map((file) => ({
      id: makeLocalId(),
      type: "local",
      file,
      name: file.name,
      previewUrl: URL.createObjectURL(file),
    }));

    setFittingImages((current) => ({
      ...current,

      [category]: [...(current[category] || []), ...newImages],
    }));
  };

  // ==========================================================
  // REMOVE PHOTO
  // ==========================================================

  const removePhoto = (category, imageId) => {
    if (submitting) {
      return;
    }

    const image = (fittingImages[category] || []).find(
      (item) => item.id === imageId,
    );

    if (!image) {
      return;
    }

    // New unsaved image
    if (image.type === "local") {
      if (image.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }
    }

    // Already saved image
    if (image.type === "existing" && image.url) {
      setRemovedExistingImages((current) => [
        ...current,
        {
          category,
          url: image.url,
        },
      ]);
    }

    setFittingImages((current) => ({
      ...current,

      [category]: (current[category] || []).filter(
        (item) => item.id !== imageId,
      ),
    }));
  };

  /** e.g
   * {
   *    beforePhotos: [
   *        "/photo/tims/fittings/abc.jpg"
   *      ]
   *    }
   */
  const buildRemoveFittingImages = () => {
    return removedExistingImages.reduce((result, image) => {
      if (!result[image.category]) {
        result[image.category] = [];
      }

      result[image.category].push(image.url);

      return result;
    }, {});
  };

  // ==========================================================
  // VALIDATION
  // ==========================================================

  const isValid = fitterIds.length > 0 && Boolean(fittingDate);

  // ==========================================================
  // UPLOAD NEW IMAGES
  // ==========================================================

  const uploadFittingImages = async (uploadedThisAttempt) => {
    /*
     * IMPORTANT:
     *
     * This contains NEW uploaded URLs only.
     *
     * Existing URLs must never be resent
     * because the update API appends arrays.
     */
    const newUploadedImages = createEmptyImages();

    const localImages = IMAGE_FIELDS.flatMap(({ key }) =>
      (fittingImages[key] || []).filter((image) => image.type === "local"),
    );

    const totalUploads = localImages.length;

    let completedUploads = 0;

    if (totalUploads > 0) {
      setUploadProgress({
        completed: 0,
        total: totalUploads,
      });
    }

    for (const { key } of IMAGE_FIELDS) {
      for (const image of fittingImages[key] || []) {
        /*
         * Existing image already belongs
         * to the fitting.
         */
        if (image.type === "existing") {
          continue;
        }

        const safeJobId = String(jobId || "job").replace(
          /[^A-Za-z0-9_-]/g,
          "_",
        );

        const filename = [
          safeJobId,
          key,
          Date.now(),
          Math.random().toString(36).slice(2, 8),
        ].join("-");

        const result = await serviceUploadPhoto({
          file: image.file,
          folder: "tims/fittings",
          filename,
        });

        if (!result?.url) {
          throw new Error("Image upload failed.");
        }

        newUploadedImages[key].push(result.url);

        uploadedThisAttempt.push(result.url);

        completedUploads += 1;

        setUploadProgress({
          completed: completedUploads,
          total: totalUploads,
        });
      }
    }

    return newUploadedImages;
  };

  // ==========================================================
  // CLEAN UP NEW UPLOADS WHEN MAIN SAVE FAILS
  // ==========================================================

  const cleanupUploadedPhotos = async (urls) => {
    if (!urls.length) {
      return;
    }

    await Promise.allSettled(urls.map((url) => serviceDeletePhoto(url)));
  };

  // ==========================================================
  // PENDING REMOVALS
  // ==========================================================

  const hasPendingRemovals =
    removedExistingImages.length > 0 || removedExistingComments.length > 0;

  // ==========================================================
  // REMOVE SAVED COMMENTS / IMAGE REFERENCES
  // ==========================================================

  const removeSavedFittingItems = async () => {
    const hasImages = removedExistingImages.length > 0;

    const hasComments = removedExistingComments.length > 0;

    if (!hasImages && !hasComments) {
      return {
        success: true,
        storageCleanupFailed: 0,
      };
    }

    const removalPayload = {};

    if (hasImages) {
      removalPayload.removeFittingImages = buildRemoveFittingImages();
    }

    if (hasComments) {
      removalPayload.removeComments = [...removedExistingComments];
    }

    console.log("Fitting removal payload:", removalPayload);

    // --------------------------------------------------------
    // 1. REMOVE REFERENCES FROM FITTING
    // --------------------------------------------------------

    try {
      await serviceRemoveCommentImageFitting(jobId, removalPayload);
    } catch (error) {
      console.error("Failed removing fitting references:", error);

      return {
        success: false,
        storageCleanupFailed: 0,
      };
    }

    // --------------------------------------------------------
    // 2. DELETE PHYSICAL IMAGE FILES
    // --------------------------------------------------------

    let storageCleanupFailed = 0;

    if (hasImages) {
      const results = await Promise.allSettled(
        removedExistingImages.map(({ url }) => serviceDeletePhoto(url)),
      );

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          console.error(
            "Failed deleting fitting image file:",
            removedExistingImages[index].url,
            result.reason,
          );
        }
      });

      storageCleanupFailed = results.filter(
        (result) => result.status === "rejected",
      ).length;
    }

    // DB references were successfully removed,
    // so clear pending queues.
    setRemovedExistingImages([]);
    setRemovedExistingComments([]);

    return {
      success: true,
      storageCleanupFailed,
    };
  };

  // ==========================================================
  // SUBMIT
  // ==========================================================

  const handleSubmit = async () => {
    if (!isValid || submitting) {
      return;
    }

    const uploadedThisAttempt = [];

    /*
     * Prevent deleting newly uploaded files
     * after the fitting itself has already
     * successfully claimed them.
     */
    let fittingSaved = false;

    try {
      setSubmitting(true);

      // ------------------------------------------------------
      // UPLOAD NEW PHOTOS
      // ------------------------------------------------------

      const uploadedImages = await uploadFittingImages(uploadedThisAttempt);

      // ------------------------------------------------------
      // BUILD NORMAL ADD / UPDATE PAYLOAD
      // ------------------------------------------------------

      const payload = {
        fitterIds,
        fittingDate,

        /*
         * New fitting:
         * all selected images are local, so
         * all uploaded URLs are included.
         *
         * Editing:
         * only newly uploaded URLs are included.
         */
        fittingImages: uploadedImages,

        /*
         * Update endpoint appends comments.
         *
         * Never resend existing comments.
         */
        comments: editing
          ? comments
              .filter((comment) => comment.type === "new")
              .map((comment) => comment.text.trim())
              .filter(Boolean)
          : comments.map((comment) => comment.text.trim()).filter(Boolean),
      };

      // ------------------------------------------------------
      // MAIN FITTING SAVE
      // ------------------------------------------------------

      const result = editing
        ? await serviceUpdateScaleFitting(jobId, payload)
        : await serviceAddScaleFitting(jobId, payload);

      /*
       * From here on, newly uploaded image
       * files belong to the saved fitting.
       */
      fittingSaved = true;

      // ------------------------------------------------------
      // REMOVE SAVED ITEMS
      // ------------------------------------------------------

      if (editing && hasPendingRemovals) {
        const removalResult = await removeSavedFittingItems();

        if (!removalResult.success) {
          toast.warn(
            "Fitting changes were saved, but some removed photos or comments could not be removed.",
          );

          onSaved?.(result);

          return;
        }

        if (removalResult.storageCleanupFailed > 0) {
          console.error(
            `${removalResult.storageCleanupFailed} removed fitting image file(s) could not be deleted from storage.`,
          );

          toast.warn(
            "Fitting updated successfully, but some removed image files could not be cleaned up from storage.",
          );

          onSaved?.(result);

          return;
        }
      }

      // ------------------------------------------------------
      // SUCCESS
      // ------------------------------------------------------

      toast.success(
        editing
          ? "Fitting updated successfully."
          : "Fitting added successfully.",
      );

      onSaved?.(result);
    } catch (error) {
      console.error("Error saving fitting:", error);

      /*
       * If the main fitting save failed,
       * remove uploads made during this attempt.
       *
       * If the fitting already saved successfully,
       * those images now belong to it.
       */
      if (!fittingSaved) {
        await cleanupUploadedPhotos(uploadedThisAttempt);
      }

      toast.error(error?.message || "Failed to save fitting.");
    } finally {
      setUploadProgress(null);
      setSubmitting(false);
    }
  };

  // ==========================================================
  // FULL FITTING IMAGE GALLERY
  // ==========================================================

  const allFittingImages = useMemo(() => {
    return IMAGE_FIELDS.flatMap((field) => {
      const images = fittingImages[field.key] || [];

      return images.map((image, index) => ({
        ...image,

        category: field.key,

        categoryLabel: field.label,

        categoryDescription: field.description,

        categoryIndex: index,

        src: image.type === "local" ? image.previewUrl : getImageUrl(image.url),
      }));
    });
  }, [fittingImages]);

  // ==========================================================
  // IMAGE VIEWER
  // ==========================================================

  const openImageViewer = (category, imageId) => {
    const index = allFittingImages.findIndex(
      (image) => image.category === category && image.id === imageId,
    );

    if (index === -1) {
      return;
    }

    setEnlargedImageIndex(index);

    setEnlargedImageUrl(allFittingImages[index].src);
  };

  const closeImageViewer = () => {
    setEnlargedImageUrl(null);
    setEnlargedImageIndex(0);
  };

  const showPreviousImage = () => {
    if (allFittingImages.length === 0) {
      closeImageViewer();

      return;
    }

    const newIndex =
      enlargedImageIndex <= 0
        ? allFittingImages.length - 1
        : enlargedImageIndex - 1;

    setEnlargedImageIndex(newIndex);

    setEnlargedImageUrl(allFittingImages[newIndex].src);
  };

  const showNextImage = () => {
    if (allFittingImages.length === 0) {
      closeImageViewer();

      return;
    }

    const newIndex =
      enlargedImageIndex >= allFittingImages.length - 1
        ? 0
        : enlargedImageIndex + 1;

    setEnlargedImageIndex(newIndex);

    setEnlargedImageUrl(allFittingImages[newIndex].src);
  };

  const enlargedCurrentImage = allFittingImages[enlargedImageIndex] || null;

  // ==========================================================
  // IMAGE VIEWER KEYBOARD CONTROLS
  // ==========================================================

  useEffect(() => {
    if (!enlargedImageUrl) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeImageViewer();

        return;
      }

      if (event.key === "ArrowLeft") {
        showPreviousImage();

        return;
      }

      if (event.key === "ArrowRight") {
        showNextImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enlargedImageUrl, enlargedImageIndex, allFittingImages]);

  // ==========================================================
  // CLOSE VIEWER WITH DRAWER
  // ==========================================================

  useEffect(() => {
    if (!open) {
      closeImageViewer();
    }
  }, [open]);

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <>
      <ModalSlideOver
        open={open}
        title={editing ? "Edit Fitting" : "Add Fitting"}
        onClose={() => {
          if (!submitting) {
            onClose?.();
          }
        }}
        footer={
          <>
            <button
              type="button"
              className="job-fitting-btn ghost"
              disabled={submitting}
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="button"
              className="job-fitting-btn primary"
              disabled={!isValid || submitting}
              onClick={handleSubmit}
            >
              {submitting
                ? uploadProgress
                  ? `Uploading ${uploadProgress.completed}/${uploadProgress.total}...`
                  : "Saving..."
                : editing
                  ? "Save Changes"
                  : "Add Fitting"}
            </button>
          </>
        }
      >
        <div className="job-fitting-drawer">
          {/* ================================================
              FITTING DETAILS
          ================================================= */}

          <section className="job-fitting-section">
            <div className="job-fitting-section-header">
              <div className="job-fitting-section-icon">
                <BsCalendar3 />
              </div>

              <div>
                <h3>Fitting Details</h3>

                <p>
                  Set the fitting date and select the technicians who completed
                  the work.
                </p>
              </div>
            </div>

            <div className="job-fitting-grid">
              <div className="job-fitting-field">
                <label htmlFor="fitting-date">
                  Fitting Date <span>*</span>
                </label>

                <input
                  id="fitting-date"
                  type="date"
                  value={fittingDate}
                  disabled={submitting}
                  onChange={(event) => setFittingDate(event.target.value)}
                />
              </div>

              <div className="job-fitting-field">
                <label>
                  Fitters <span>*</span>
                </label>

                {loadingUsers ? (
                  <div className="job-fitting-loading">Loading users...</div>
                ) : users.length === 0 ? (
                  <div className="job-fitting-loading">No users available.</div>
                ) : (
                  <select
                    value=""
                    disabled={submitting}
                    onChange={(event) => addFitter(event.target.value)}
                  >
                    <option value="" disabled>
                      Select a fitter...
                    </option>

                    {users.map((user) => {
                      const id = getId(user);

                      const selected = fitterIds.includes(id);

                      return (
                        <option key={id} value={id} disabled={selected}>
                          {getUserLabel(user)}
                          {selected ? " - Selected" : ""}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            </div>

            {selectedUsers.length > 0 && (
              <div className="job-fitting-selected">
                <div className="job-fitting-selected-title">
                  <BsPeople />

                  <span>Selected Fitters</span>
                </div>

                <div className="job-fitting-selected-list">
                  {selectedUsers.map((user) => {
                    const id = getId(user);

                    return (
                      <span key={id} className="job-fitting-selected-item">
                        {getUserLabel(user)}

                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => removeFitter(id)}
                          aria-label={`Remove ${getUserLabel(user)}`}
                          title="Remove fitter"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* ================================================
              PHOTOS
          ================================================= */}

          <section className="job-fitting-section">
            <div className="job-fitting-section-header">
              <div className="job-fitting-section-icon">
                <BsImages />
              </div>

              <div>
                <h3>Fitting Photos</h3>

                <p>
                  Take photos directly from a phone camera or choose multiple
                  photos from the device.
                </p>
              </div>
            </div>

            <div className="job-fitting-photo-groups">
              {IMAGE_FIELDS.map((imageField) => (
                <PhotoGroup
                  key={imageField.key}
                  field={imageField}
                  images={fittingImages[imageField.key] || []}
                  disabled={submitting}
                  onFiles={handleFiles}
                  onRemove={removePhoto}
                  onOpenImage={openImageViewer}
                />
              ))}
            </div>
          </section>

          {/* ================================================
              AUTOMATIC CHECKLIST
          ================================================= */}

          <section className="job-fitting-section">
            <div className="job-fitting-section-header">
              <div className="job-fitting-section-icon">
                <BsCheck2Circle />
              </div>

              <div>
                <h3>Fitting Checklist</h3>

                <p>
                  Checklist completion is automatically determined by the photos
                  added above.
                </p>
              </div>
            </div>

            <div className="job-fitting-checklist">
              {CHECKLIST_FIELDS.map((item) => {
                const count = fittingImages[item.imageKey]?.length || 0;
                const complete = count > 0;
                return (
                  <div
                    key={item.imageKey}
                    className={`job-fitting-check ${complete ? "complete" : ""}`}
                  >
                    <div className="job-fitting-check-icon">
                      {complete ? <BsCheck2Circle /> : <BsCircle />}
                    </div>

                    <div className="job-fitting-check-content">
                      <strong>{item.label}</strong>

                      <span>
                        {count > 0
                          ? `${count} photo${count === 1 ? "" : "s"}`
                          : "No photos"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ================================================
              COMMENTS
          ================================================= */}

          <section className="job-fitting-section">
            <div className="job-fitting-section-header">
              <div className="job-fitting-section-icon">
                <BsChatLeftText />
              </div>

              <div>
                <h3>Fitting Comments</h3>

                <p>Add individual notes about the fitting work performed.</p>
              </div>
            </div>

            {comments.length > 0 && (
              <div className="job-fitting-comments">
                {comments.map((comment, index) => (
                  <div key={comment.id} className="job-fitting-comment">
                    <span className="job-fitting-comment-number">
                      {index + 1}
                    </span>

                    <span className="job-fitting-comment-text">
                      {comment.text}
                    </span>

                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => removeComment(comment.id)}
                      aria-label={`Remove comment ${index + 1}`}
                      title="Remove comment"
                    >
                      <BsTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="job-fitting-comment-add">
              <input
                type="text"
                value={commentDraft}
                disabled={submitting}
                placeholder="Enter a fitting comment..."
                onChange={(event) => setCommentDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();

                    addComment();
                  }
                }}
              />

              <button
                type="button"
                className="job-fitting-btn ghost"
                disabled={submitting || !commentDraft.trim()}
                onClick={addComment}
              >
                <BsPlusLg />
                Add Comment
              </button>
            </div>
          </section>
        </div>
      </ModalSlideOver>

      {/* ================================================
          FULLSCREEN IMAGE VIEWER
      ================================================= */}

      {enlargedImageUrl &&
        createPortal(
          <div
            className="job-fitting-image-viewer"
            role="dialog"
            aria-modal="true"
            aria-label={enlargedCurrentImage?.categoryLabel || "Fitting photo"}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeImageViewer();
              }
            }}
          >
            <div className="job-fitting-image-viewer-panel">
              {/* HEADER */}

              <div className="job-fitting-image-viewer-header">
                <div>
                  <h3>
                    {enlargedCurrentImage?.categoryLabel || "Fitting Photo"}
                  </h3>

                  <span>{enlargedCurrentImage?.categoryDescription}</span>
                </div>

                <button
                  type="button"
                  className="job-fitting-image-viewer-close"
                  onClick={closeImageViewer}
                  aria-label="Close image viewer"
                >
                  <BsXLg />
                </button>
              </div>

              {/* IMAGE */}

              <div className="job-fitting-image-viewer-content">
                {allFittingImages.length > 1 && (
                  <button
                    type="button"
                    className="job-fitting-image-viewer-nav previous"
                    onClick={showPreviousImage}
                    aria-label="Previous image"
                  >
                    <BsArrowLeft />
                  </button>
                )}

                <img
                  src={enlargedImageUrl}
                  alt={
                    enlargedCurrentImage
                      ? `${enlargedCurrentImage.categoryLabel} ${
                          enlargedCurrentImage.categoryIndex + 1
                        }`
                      : "Fitting photo"
                  }
                />

                {allFittingImages.length > 1 && (
                  <button
                    type="button"
                    className="job-fitting-image-viewer-nav next"
                    onClick={showNextImage}
                    aria-label="Next image"
                  >
                    <BsArrowRight />
                  </button>
                )}
              </div>

              {/* FOOTER */}

              <div className="job-fitting-image-viewer-footer">
                <div>
                  <span>
                    Photo {enlargedImageIndex + 1} of {allFittingImages.length}
                  </span>

                  {enlargedCurrentImage && (
                    <span>
                      {enlargedCurrentImage.categoryIndex + 1} of{" "}
                      {fittingImages[enlargedCurrentImage.category]?.length} in
                      this section
                    </span>
                  )}
                </div>

                {enlargedCurrentImage?.type === "local" && (
                  <span className="job-fitting-image-viewer-new">
                    New photo
                  </span>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// ============================================================
// PHOTO GROUP
// ============================================================

const PhotoGroup = ({
  field,
  images,
  disabled,
  onFiles,
  onRemove,
  onOpenImage,
}) => {
  const cameraInputId = `fitting-${field.key}-camera`;

  const galleryInputId = `fitting-${field.key}-gallery`;

  return (
    <div className="job-fitting-photo-group">
      <div className="job-fitting-photo-header">
        <div>
          <strong>{field.label}</strong>

          <span>{field.description}</span>
        </div>

        <span className="job-fitting-photo-count">{images.length}</span>
      </div>

      {images.length > 0 ? (
        <div className="job-fitting-photo-grid">
          {images.map((image, index) => {
            const src =
              image.type === "local"
                ? image.previewUrl
                : getImageUrl(image.url);

            return (
              <div className="job-fitting-photo" key={image.id}>
                <button
                  type="button"
                  className="job-fitting-photo-view"
                  onClick={() => onOpenImage(field.key, image.id)}
                  aria-label={`View ${field.label} photo ${index + 1}`}
                >
                  <img src={src} alt={`${field.label} ${index + 1}`} />
                </button>

                <button
                  type="button"
                  className="job-fitting-photo-remove"
                  onClick={() => onRemove(field.key, image.id)}
                  disabled={disabled}
                  aria-label="Remove photo"
                  title="Remove photo"
                >
                  <BsTrash />
                </button>

                {image.type === "local" && (
                  <span className="job-fitting-photo-new">New</span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="job-fitting-photo-empty">
          <BsImages />

          <span>No photos added yet.</span>
        </div>
      )}

      <div className="job-fitting-photo-actions">
        {/* PHONE CAMERA */}

        <label
          htmlFor={cameraInputId}
          className={`job-fitting-photo-btn ${disabled ? "disabled" : ""}`}
        >
          <BsCamera />
          Take Photo
        </label>

        <input
          id={cameraInputId}
          type="file"
          accept="image/*"
          capture="environment"
          className="job-fitting-photo-input"
          disabled={disabled}
          onChange={(event) => onFiles(field.key, event)}
        />

        {/* GALLERY */}

        <label
          htmlFor={galleryInputId}
          className={`job-fitting-photo-btn ${disabled ? "disabled" : ""}`}
        >
          <BsImages />
          Choose Photos
        </label>

        <input
          id={galleryInputId}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif"
          multiple
          className="job-fitting-photo-input"
          disabled={disabled}
          onChange={(event) => onFiles(field.key, event)}
        />
      </div>
    </div>
  );
};

export default JobSheetDrawerFitting;
